// T055: Combat Resolver

import type {
  GameState,
  Card,
  CardDefinition,
  BattlefieldPosition,
  CombatResult,
  CombatInfo,
} from '../../types';
import { EffectResolver } from '../effects/EffectResolver';
import { effectTriggerSystem } from '../effects/EffectTriggerSystem';
import { hasDestroyResistance } from '../effects/handlers/CombatHandlers';

export interface CombatResolutionResult {
  state: GameState;
  combatResults: CombatResult[];
  destroyedUnits: string[];
}

/**
 * Get the slot at a given position.
 */
function getSlot(state: GameState, position: BattlefieldPosition) {
  const side = position.side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
  const line = position.row === 'front' ? side.frontLine : side.backLine;
  return line[position.column - 1];
}

/**
 * Get ATK value for a card from definitions.
 */
function getAtk(card: Card, definitions: Map<string, CardDefinition>): number {
  const def = definitions.get(card.definitionId);
  return def?.atk ?? 0;
}

/**
 * Check if a unit has a specific keyword.
 */
function hasKeyword(unit: Card, keyword: string): boolean {
  return unit.keywords?.includes(keyword) ?? false;
}

/**
 * Check if a unit has any of the given keywords.
 */
function hasAnyKeyword(unit: Card, keywords: string[]): boolean {
  return keywords.some(kw => hasKeyword(unit, kw));
}

/**
 * Find adjacent columns (1-indexed, max 5).
 */
function getAdjacentColumns(column: number): number[] {
  const adj: number[] = [];
  if (column > 1) adj.push(column - 1);
  if (column < 5) adj.push(column + 1);
  return adj;
}

/**
 * Check if a unit at `targetPosition` is covered by a 掩護 (cover) unit in the front row.
 * Cover protects adjacent units from being targeted.
 */
function isCoveredByUnit(
  state: GameState,
  targetPosition: BattlefieldPosition
): boolean {
  const side = targetPosition.side;
  const adjacentCols = getAdjacentColumns(targetPosition.column);

  for (const col of adjacentCols) {
    // Check front row for cover units
    const coverPos: BattlefieldPosition = { side, row: 'front', column: col as 1 | 2 | 3 | 4 | 5 };
    const coverSlot = getSlot(state, coverPos);
    if (coverSlot.unit && hasKeyword(coverSlot.unit, '掩護')) {
      return true;
    }
  }

  // Also check same column front row
  if (targetPosition.row === 'back') {
    const frontPos: BattlefieldPosition = { side, row: 'front', column: targetPosition.column };
    const frontSlot = getSlot(state, frontPos);
    if (frontSlot.unit && hasKeyword(frontSlot.unit, '掩護')) {
      return true;
    }
  }

  return false;
}

/**
 * Find the cover unit protecting a given position (for redirecting attacks).
 */
function findCoverUnit(
  state: GameState,
  targetPosition: BattlefieldPosition
): { unit: Card; position: BattlefieldPosition } | null {
  const side = targetPosition.side;
  const adjacentCols = getAdjacentColumns(targetPosition.column);

  // Check adjacent front row units
  for (const col of adjacentCols) {
    const coverPos: BattlefieldPosition = { side, row: 'front', column: col as 1 | 2 | 3 | 4 | 5 };
    const coverSlot = getSlot(state, coverPos);
    if (coverSlot.unit && hasKeyword(coverSlot.unit, '掩護')) {
      return { unit: coverSlot.unit, position: coverPos };
    }
  }

  // Check same column front row
  if (targetPosition.row === 'back') {
    const frontPos: BattlefieldPosition = { side, row: 'front', column: targetPosition.column };
    const frontSlot = getSlot(state, frontPos);
    if (frontSlot.unit && hasKeyword(frontSlot.unit, '掩護')) {
      return { unit: frontSlot.unit, position: frontPos };
    }
  }

  return null;
}

/**
 * Check if the given column has a 阻擋 (block) unit in the front row on the enemy side.
 */
function findBlocker(
  state: GameState,
  attackerPosition: BattlefieldPosition
): { unit: Card; position: BattlefieldPosition } | null {
  const enemySide = attackerPosition.side === 'player1' ? 'player2' : 'player1';
  const column = attackerPosition.column;

  const frontPosition: BattlefieldPosition = { side: enemySide, row: 'front', column };
  const frontSlot = getSlot(state, frontPosition);
  if (frontSlot.unit && hasKeyword(frontSlot.unit, '阻擋')) {
    return { unit: frontSlot.unit, position: frontPosition };
  }

  return null;
}

/**
 * Find defender for an attacker, considering combat keywords:
 * - 偷襲 (ambush/sneak attack): bypass front row, attack back row directly
 * - 飛行 (flying): can attack back row
 * - 阻擋 (block): must attack blocker first
 * - 掩護 (cover): redirect attack to cover unit
 * - 遠程攻擊 (ranged): back row can attack (handled in canAttack check)
 */
function findDefender(
  state: GameState,
  attackerPosition: BattlefieldPosition,
  attacker?: Card
): { unit: Card; position: BattlefieldPosition } | null {
  const enemySide = attackerPosition.side === 'player1' ? 'player2' : 'player1';
  const column = attackerPosition.column;

  const canBypassFront = attacker && hasAnyKeyword(attacker, ['偷襲', '飛行']);
  const hasArmorBreak = attacker && hasKeyword(attacker, '破甲');

  // Check for blocker in the column (阻擋 forces attack on blocker)
  // armorBreak (破甲) ignores block
  if (!hasArmorBreak) {
    const blocker = findBlocker(state, attackerPosition);
    if (blocker) {
      return blocker;
    }
  }

  // Check front row first (unless attacker can bypass)
  const frontPosition: BattlefieldPosition = { side: enemySide, row: 'front', column };
  const frontSlot = getSlot(state, frontPosition);

  if (canBypassFront) {
    // 偷襲/飛行: prefer back row target
    const backPosition: BattlefieldPosition = { side: enemySide, row: 'back', column };
    const backSlot = getSlot(state, backPosition);
    if (backSlot.unit) {
      // Check if covered (掩護)
      if (!hasArmorBreak && isCoveredByUnit(state, backPosition)) {
        const coverUnit = findCoverUnit(state, backPosition);
        if (coverUnit) {
          return coverUnit;
        }
      }
      return { unit: backSlot.unit, position: backPosition };
    }
    // If no back row target, fall through to front
    if (frontSlot.unit) {
      return { unit: frontSlot.unit, position: frontPosition };
    }
  } else {
    // Normal: front row first
    if (frontSlot.unit) {
      return { unit: frontSlot.unit, position: frontPosition };
    }

    // Check back row if front is empty
    const backPosition: BattlefieldPosition = { side: enemySide, row: 'back', column };
    const backSlot = getSlot(state, backPosition);
    if (backSlot.unit) {
      // Check if covered (掩護)
      if (!hasArmorBreak && isCoveredByUnit(state, backPosition)) {
        const coverUnit = findCoverUnit(state, backPosition);
        if (coverUnit) {
          return coverUnit;
        }
      }
      return { unit: backSlot.unit, position: backPosition };
    }
  }

  return null;
}

/**
 * Remove a unit from the battlefield.
 */
function removeFromBattlefield(state: GameState, position: BattlefieldPosition): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  newLine[position.column - 1] = { ...newLine[position.column - 1], unit: null };
  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

/**
 * Add a card to a player's graveyard.
 */
function addToGraveyard(state: GameState, card: Card): GameState {
  const isPlayer1 = card.ownerId === state.player1.id;
  const graveyardCard: Card = { ...card, location: 'graveyard', position: undefined };

  if (isPlayer1) {
    return {
      ...state,
      player1: {
        ...state.player1,
        graveyard: [...state.player1.graveyard, graveyardCard],
      },
    };
  } else {
    return {
      ...state,
      player2: {
        ...state.player2,
        graveyard: [...state.player2.graveyard, graveyardCard],
      },
    };
  }
}

/**
 * Update a unit's orientation on the battlefield.
 */
function setUnitOrientation(
  state: GameState,
  position: BattlefieldPosition,
  orientation: 'upright' | 'tilted' | 'tapped'
): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  const slot = newLine[position.column - 1];

  if (slot.unit) {
    newLine[position.column - 1] = {
      ...slot,
      unit: { ...slot.unit, orientation },
    };
  }

  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

/**
 * Apply ATK modifiers from state changes.
 */
function applyAtkModifiers(
  attackerAtk: number,
  defenderAtk: number,
  attackerEffects: import('../../types').StateChange[],
  defenderEffects: import('../../types').StateChange[],
  attackerId: string,
  defenderId: string
): { modifiedAttackerAtk: number; modifiedDefenderAtk: number } {
  let modifiedAttackerAtk = attackerAtk;
  let modifiedDefenderAtk = defenderAtk;

  // Apply attacker's effects
  for (const change of attackerEffects) {
    if (change.type === 'CHANGE_ATK') {
      if (change.targetId === attackerId) {
        modifiedAttackerAtk += change.value as number;
      } else if (change.targetId === defenderId) {
        modifiedDefenderAtk += change.value as number;
      }
    }
  }

  // Apply defender's effects
  for (const change of defenderEffects) {
    if (change.type === 'CHANGE_ATK') {
      if (change.targetId === attackerId) {
        modifiedAttackerAtk += change.value as number;
      } else if (change.targetId === defenderId) {
        modifiedDefenderAtk += change.value as number;
      }
    }
  }

  return { modifiedAttackerAtk, modifiedDefenderAtk };
}

/**
 * Check if unit has pierce effect (unblockable).
 * Reserved for future use when defense phase allows blocking.
 */
export function hasPierceEffect(stateChanges: import('../../types').StateChange[]): boolean {
  return stateChanges.some((change) => {
    if (change.type !== 'ADD_EFFECT') return false;
    const value = change.value as Record<string, unknown> | undefined;
    return value?.piercing === true;
  });
}

/**
 * Check if destroyed unit should go to forest instead of graveyard.
 */
function shouldGoToForest(stateChanges: import('../../types').StateChange[], targetId: string): boolean {
  return stateChanges.some((change) => {
    if (change.type !== 'ADD_EFFECT' || change.targetId !== targetId) return false;
    const value = change.value as Record<string, unknown> | undefined;
    return value?.poisoned === true || value?.dismantled === true;
  });
}

/**
 * Add a card to a player's forest.
 */
function addToForest(state: GameState, card: Card): GameState {
  const isPlayer1 = card.ownerId === state.player1.id;
  const forestCard: Card = { ...card, location: 'forest', position: undefined };

  if (isPlayer1) {
    return {
      ...state,
      player1: {
        ...state.player1,
        forest: [...state.player1.forest, forestCard],
      },
    };
  } else {
    return {
      ...state,
      player2: {
        ...state.player2,
        forest: [...state.player2.forest, forestCard],
      },
    };
  }
}

/**
 * Resolve combat between attacker and defender.
 * Integrates effect system for combat modifications.
 */
function resolveSingleCombat(
  state: GameState,
  attacker: Card,
  attackerPosition: BattlefieldPosition,
  defender: Card,
  defenderPosition: BattlefieldPosition,
  definitions: Map<string, CardDefinition>
): { state: GameState; result: CombatResult; destroyedUnits: string[] } {
  const baseAttackerAtk = getAtk(attacker, definitions);
  const baseDefenderAtk = getAtk(defender, definitions);
  const destroyedUnits: string[] = [];

  // Build combat info for effect resolution
  const combatInfo: CombatInfo = {
    attackerId: attacker.id,
    defenderId: defender.id,
    column: attackerPosition.column,
    attackerAtk: baseAttackerAtk,
    defenderAtk: baseDefenderAtk,
  };

  // Trigger attacker's onAttack effects
  const attackerEffectResult = EffectResolver.resolve(state, 'defense', {
    trigger: 'onAttack',
    sourceCard: attacker,
    targetCards: [defender],
    combatInfo,
  });

  // Trigger defender's onDefend effects
  const defenderEffectResult = EffectResolver.resolve(state, 'defense', {
    trigger: 'onDefend',
    sourceCard: defender,
    targetCards: [attacker],
    combatInfo,
  });

  // Apply ATK modifiers from effects
  let { modifiedAttackerAtk, modifiedDefenderAtk } = applyAtkModifiers(
    baseAttackerAtk,
    baseDefenderAtk,
    attackerEffectResult.allChanges,
    defenderEffectResult.allChanges,
    attacker.id,
    defender.id
  );

  // 破甲 (armorBreak): ignore defender's onDefend ATK modifications
  if (hasKeyword(attacker, '破甲')) {
    // Reverse any ATK buffs the defender gave itself during defense
    for (const change of defenderEffectResult.allChanges) {
      if (change.type === 'CHANGE_ATK' && change.targetId === defender.id) {
        modifiedDefenderAtk -= (change.value as number);
      }
    }
    // Ensure ATK doesn't go below base
    if (modifiedDefenderAtk < 0) modifiedDefenderAtk = 0;
  }

  // ATK modifiers from card runtime state
  if (attacker.atkModifier) {
    modifiedAttackerAtk += attacker.atkModifier;
  }
  if (defender.atkModifier) {
    // ArmorBreak also ignores defender's runtime ATK buffs (if positive)
    if (hasKeyword(attacker, '破甲') && defender.atkModifier > 0) {
      // Ignore positive ATK modifier on defender
    } else {
      modifiedDefenderAtk += defender.atkModifier;
    }
  }

  // Trigger combat resolve effects
  const combatResolveResult = EffectResolver.resolve(state, 'defense', {
    trigger: 'onCombatResolve',
    sourceCard: attacker,
    targetCards: [defender],
    combatInfo: { ...combatInfo, attackerAtk: modifiedAttackerAtk, defenderAtk: modifiedDefenderAtk },
  });

  // Combine all effect changes
  const allEffectChanges = [
    ...attackerEffectResult.allChanges,
    ...defenderEffectResult.allChanges,
    ...combatResolveResult.allChanges,
  ];

  let newState = state;
  let winner: 'attacker' | 'defender';
  let destroyedCardId: string;

  if (modifiedAttackerAtk > modifiedDefenderAtk) {
    // Attacker wins
    winner = 'attacker';
    destroyedCardId = defender.id;

    // Check destroy resistance before removing defender
    if (hasDestroyResistance(defender.keywords)) {
      // Defender resists destruction — attacker still taps
      newState = setUnitOrientation(newState, attackerPosition, 'tapped');
    } else {
      destroyedUnits.push(defender.id);
      newState = removeFromBattlefield(newState, defenderPosition);

      // Check for poison/dismantle - send to forest instead of graveyard
      if (shouldGoToForest(allEffectChanges, defender.id)) {
        newState = addToForest(newState, defender);
      } else {
        newState = addToGraveyard(newState, defender);
      }

      // Tap the attacker
      newState = setUnitOrientation(newState, attackerPosition, 'tapped');

      // 貫穿 (pierce): excess damage hits back row unit in same column
      if (hasKeyword(attacker, '貫穿') && defenderPosition.row === 'front') {
        const excessDamage = modifiedAttackerAtk - modifiedDefenderAtk;
        if (excessDamage > 0) {
          const enemySide = defenderPosition.side;
          const backPosition: BattlefieldPosition = {
            side: enemySide,
            row: 'back',
            column: defenderPosition.column,
          };
          const backSlot = getSlot(newState, backPosition);
          if (backSlot.unit) {
            const backDefenderAtk = getAtk(backSlot.unit, definitions);
            if (excessDamage > backDefenderAtk) {
              // Pierce kills back row unit too
              if (!hasDestroyResistance(backSlot.unit.keywords)) {
                destroyedUnits.push(backSlot.unit.id);
                newState = removeFromBattlefield(newState, backPosition);
                newState = addToGraveyard(newState, backSlot.unit);
              }
            }
          }
        }
      }

      // 滑行 (slide): attacker moves to the defeated defender's position after combat
      if (hasKeyword(attacker, '滑行') || hasKeyword(attacker, '滑壘')) {
        // Move attacker to defender's old position (which is now empty)
        const attackerSideKey = attackerPosition.side === 'player1' ? 'player1Side' : 'player2Side';
        const attackerLineKey = attackerPosition.row === 'front' ? 'frontLine' : 'backLine';

        // Get current attacker from state (it's been tapped)
        const currentAttackerSlot = getSlot(newState, attackerPosition);
        if (currentAttackerSlot.unit) {
          // Note: slide moves to the defender's position which may be on the enemy side
          // In this game, the attacker stays on their own side but we can interpret slide
          // as: after winning, move forward one position (front row if was back row)
          if (attackerPosition.row === 'back') {
            // Move to front row same column (if empty)
            const frontPos: BattlefieldPosition = {
              side: attackerPosition.side,
              row: 'front',
              column: attackerPosition.column,
            };
            const frontSlot = getSlot(newState, frontPos);
            if (!frontSlot.unit) {
              const slideUnit = currentAttackerSlot.unit;
              // Clear old position
              const newBf = { ...newState.battlefield };
              const newSide = { ...newBf[attackerSideKey] };
              const oldLine = [...newSide[attackerLineKey]];
              oldLine[attackerPosition.column - 1] = { ...oldLine[attackerPosition.column - 1], unit: null };
              newSide[attackerLineKey] = oldLine;

              // Place at front
              const newFrontLine = [...newSide.frontLine];
              newFrontLine[frontPos.column - 1] = {
                ...newFrontLine[frontPos.column - 1],
                unit: { ...slideUnit, position: frontPos },
              };
              newSide.frontLine = newFrontLine;
              newBf[attackerSideKey] = newSide;
              newState = { ...newState, battlefield: newBf };
            }
          }
        }
      }
    }
  } else if (modifiedDefenderAtk > modifiedAttackerAtk) {
    // Defender wins
    winner = 'defender';
    destroyedCardId = attacker.id;

    // Check destroy resistance before removing attacker
    if (hasDestroyResistance(attacker.keywords)) {
      // Attacker resists destruction — just tap
      newState = setUnitOrientation(newState, attackerPosition, 'tapped');
    } else {
      destroyedUnits.push(attacker.id);
      newState = removeFromBattlefield(newState, attackerPosition);
      newState = addToGraveyard(newState, attacker);
    }
  } else {
    // Tie (equal ATK) - both destroyed (mutual destruction)
    winner = 'attacker';
    destroyedCardId = defender.id;

    // Destroy defender
    if (hasDestroyResistance(defender.keywords)) {
      // Defender resists destruction
    } else {
      destroyedUnits.push(defender.id);
      newState = removeFromBattlefield(newState, defenderPosition);

      if (shouldGoToForest(allEffectChanges, defender.id)) {
        newState = addToForest(newState, defender);
      } else {
        newState = addToGraveyard(newState, defender);
      }
    }

    // Destroy attacker
    if (hasDestroyResistance(attacker.keywords)) {
      // Attacker resists destruction
    } else {
      destroyedUnits.push(attacker.id);
      newState = removeFromBattlefield(newState, attackerPosition);
      newState = addToGraveyard(newState, attacker);
    }
  }

  // Trigger onDestroy effects for destroyed units
  for (const destroyedId of destroyedUnits) {
    const destroyedCard = destroyedId === attacker.id ? attacker : defender;
    const destroyedPosition = destroyedId === attacker.id ? attackerPosition : defenderPosition;
    const destroyer = destroyedId === attacker.id ? defender : attacker;
    
    // 舊的效果系統
    EffectResolver.resolve(newState, 'defense', {
      trigger: 'onDestroy',
      sourceCard: destroyedCard,
    });
    
    // 新的觸發系統 - 處理效果文字
    const triggerResult = effectTriggerSystem.triggerOnDestroy(
      newState,
      destroyedCard,
      destroyedPosition,
      destroyer
    );
    
    if (triggerResult.immediateActions.length > 0) {
      newState = effectTriggerSystem.executeActions(newState, triggerResult.immediateActions);
    }
  }
  
  // 觸發戰鬥成功效果 (勝利者)
  if (winner === 'attacker') {
    const triggerResult = effectTriggerSystem.triggerOnCombatSuccess(
      newState,
      attacker,
      attackerPosition,
      defender
    );
    if (triggerResult.immediateActions.length > 0) {
      newState = effectTriggerSystem.executeActions(newState, triggerResult.immediateActions);
    }
  } else {
    // winner === 'defender'
    const triggerResult = effectTriggerSystem.triggerOnCombatSuccess(
      newState,
      defender,
      defenderPosition,
      attacker
    );
    if (triggerResult.immediateActions.length > 0) {
      newState = effectTriggerSystem.executeActions(newState, triggerResult.immediateActions);
    }
  }

  const result: CombatResult = {
    attackerId: attacker.id,
    defenderId: defender.id,
    attackerAtk: modifiedAttackerAtk,
    defenderAtk: modifiedDefenderAtk,
    winner,
    destroyedCardId,
  };

  return { state: newState, result, destroyedUnits };
}

/**
 * Resolve all pending combat based on attack declarations.
 */
export function resolveAllCombat(
  state: GameState,
  definitions: Map<string, CardDefinition>
): CombatResolutionResult {
  let newState = state;
  const combatResults: CombatResult[] = [];
  const destroyedUnits: string[] = [];

  // Process each attack declaration
  for (const declaration of state.attackDeclarations) {
    const attackerSlot = getSlot(newState, declaration.attackerPosition);
    const attacker = attackerSlot.unit;

    // Skip if attacker no longer exists (destroyed in earlier combat)
    if (!attacker) continue;

    // Find defender (considering combat keywords like 偷襲, 飛行, 阻擋, 掩護)
    const defenderInfo = findDefender(newState, declaration.attackerPosition, attacker);

    if (!defenderInfo) {
      // No enemy to attack - just tap the attacker
      newState = setUnitOrientation(newState, declaration.attackerPosition, 'tapped');
      continue;
    }

    // Resolve combat
    const combatOutcome = resolveSingleCombat(
      newState,
      attacker,
      declaration.attackerPosition,
      defenderInfo.unit,
      defenderInfo.position,
      definitions
    );

    newState = combatOutcome.state;
    combatResults.push(combatOutcome.result);
    destroyedUnits.push(...combatOutcome.destroyedUnits);
  }

  // Clear attack declarations after combat
  newState = { ...newState, attackDeclarations: [] };

  return { state: newState, combatResults, destroyedUnits };
}

/**
 * Untap all units for a player at end of turn.
 * Also clears summonedThisTurn flag so units can attack next turn.
 */
export function untapAllUnits(state: GameState, playerId: string): GameState {
  const sideKey = playerId === state.player1.id ? 'player1Side' : 'player2Side';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };

  // Untap front line and clear summonedThisTurn
  newSide.frontLine = newSide.frontLine.map((slot) => ({
    ...slot,
    unit: slot.unit ? { ...slot.unit, orientation: 'upright' as const, summonedThisTurn: false } : null,
  }));

  // Untap back line and clear summonedThisTurn
  newSide.backLine = newSide.backLine.map((slot) => ({
    ...slot,
    unit: slot.unit ? { ...slot.unit, orientation: 'upright' as const, summonedThisTurn: false } : null,
  }));

  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

/**
 * Check if there are pending attacks to resolve.
 */
export function hasPendingCombat(state: GameState): boolean {
  return state.attackDeclarations.length > 0;
}

/**
 * Get combat preview for an attack declaration.
 */
export function getCombatPreview(
  state: GameState,
  attackerPosition: BattlefieldPosition,
  definitions: Map<string, CardDefinition>
): { attacker: Card; defender: Card | null; attackerAtk: number; defenderAtk: number } | null {
  const attackerSlot = getSlot(state, attackerPosition);
  if (!attackerSlot.unit) return null;

  const attacker = attackerSlot.unit;
  const attackerAtk = getAtk(attacker, definitions);

  const defenderInfo = findDefender(state, attackerPosition, attacker);
  if (!defenderInfo) {
    return { attacker, defender: null, attackerAtk, defenderAtk: 0 };
  }

  const defenderAtk = getAtk(defenderInfo.unit, definitions);
  return { attacker, defender: defenderInfo.unit, attackerAtk, defenderAtk };
}

// Re-export as object for backwards compatibility
export const CombatResolver = {
  resolveAllCombat,
  untapAllUnits,
  hasPendingCombat,
  getCombatPreview,
};
